import { createSignal, For, Show, onCleanup } from 'solid-js';
import { useNavigate } from '@solidjs/router';
import { locale, setLocale, LOCALE_LABELS } from '@/shared/i18n';
import { isPublicPage } from '@/shared/i18n';
import { isAuthenticated } from '@/features/auth/store/session.store';
import { updateUserLocale } from '@/shared/api/locale.api';
import {
  availableLocalesResource,
  ensureAvailableLocalesLoaded,
} from '@/shared/stores/availableLocales.store';

const KNOWN_FLAGS: Record<string, string> = {
  vi: '🇻🇳',
  en: '🇺🇸',
  ja: '🇯🇵',
  ko: '🇰🇷',
  zh: '🇨🇳',
  fr: '🇫🇷',
  de: '🇩🇪',
  es: '🇪🇸',
  pt: '🇵🇹',
  th: '🇹🇭',
};

function getFlag(l: string): string {
  return KNOWN_FLAGS[l] ?? '🌐';
}

function getLabel(l: string): string {
  if (LOCALE_LABELS[l]) return LOCALE_LABELS[l];
  try {
    return (
      new Intl.DisplayNames(['en'], { type: 'language' }).of(l) ??
      l.toUpperCase()
    );
  } catch {
    return l.toUpperCase();
  }
}

/**
 * Compact language switcher for the top navigation bar.
 * Shows a dropdown of all locales supported by the backend.
 * When logged in, also persists the preference to the backend.
 */
export function LanguageSwitcher(props: { onDark?: boolean } = {}) {
  const [open, setOpen] = createSignal(false);
  const navigate = useNavigate();

  const locales = () =>
    availableLocalesResource.state === 'ready' &&
    (availableLocalesResource() ?? []).length > 0
      ? availableLocalesResource()!
      : ['vi', 'en'];

  const toggleOpen = () => {
    if (!open()) {
      ensureAvailableLocalesLoaded();
    }
    setOpen((v) => !v);
  };

  const select = (l: string) => {
    if (isPublicPage()) {
      // On public pages, navigate to the locale-prefixed URL instead of just
      // swapping the signal — this gives each locale a distinct, crawlable URL.
      const path = window.location.pathname;
      // Strip existing /vi prefix if present
      const enPath = path.startsWith('/vi/')
        ? path.slice(3)
        : path === '/vi'
          ? '/'
          : path;
      const targetPath = l === 'vi' ? `/vi${enPath}` : enPath;
      setLocale(l);
      navigate(targetPath, { replace: true });
    } else {
      setLocale(l);
      if (isAuthenticated()) updateUserLocale(l);
    }
    setOpen(false);
  };

  let containerRef: HTMLDivElement | undefined;
  const handleClickOutside = (e: MouseEvent) => {
    if (containerRef && !containerRef.contains(e.target as Node)) {
      setOpen(false);
    }
  };
  document.addEventListener('mousedown', handleClickOutside);
  onCleanup(() =>
    document.removeEventListener('mousedown', handleClickOutside)
  );

  return (
    <div ref={containerRef} class="relative">
      <button
        onClick={toggleOpen}
        title={getLabel(locale())}
        aria-label={`Switch language — current: ${getLabel(locale())}`}
        aria-expanded={open()}
        class={`flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-sm font-medium transition-colors focus:outline-none focus:ring-2 ${props.onDark ? 'text-white/80 hover:bg-white/10 hover:text-white focus:ring-white/30' : 'text-text-secondary hover:bg-bg-hover focus:ring-border-focus'}`}
      >
        <span class="text-base leading-none">{getFlag(locale())}</span>
        <span class="hidden text-xs font-semibold uppercase sm:block">
          {locale()}
        </span>
        <svg
          class="h-3 w-3 opacity-60"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      <Show when={open()}>
        <div class="absolute right-0 top-full z-50 mt-1 min-w-[160px] rounded-lg border border-border-default bg-bg-surface p-1 shadow-lg">
          <For each={locales()}>
            {(l) => (
              <button
                onClick={() => select(l)}
                class={`flex w-full items-center gap-2 rounded-md px-3 py-1.5 text-sm transition-colors ${
                  locale() === l
                    ? 'bg-accent-primary text-text-inverse'
                    : 'text-text-primary hover:bg-bg-hover'
                }`}
              >
                <span>{getFlag(l)}</span>
                <span class="flex-1 text-left font-medium">{getLabel(l)}</span>
                <span class="text-xs uppercase opacity-60">{l}</span>
              </button>
            )}
          </For>
        </div>
      </Show>
    </div>
  );
}

/**
 * Dropdown-style language switcher (for use in smaller screens / menus).
 */
export function LanguageSwitcherSelect() {
  const navigate = useNavigate();

  const locales = () =>
    availableLocalesResource.state === 'ready' &&
    (availableLocalesResource() ?? []).length > 0
      ? availableLocalesResource()!
      : ['vi', 'en'];

  const select = (l: string) => {
    if (isPublicPage()) {
      const path = window.location.pathname;
      const enPath = path.startsWith('/vi/')
        ? path.slice(3)
        : path === '/vi'
          ? '/'
          : path;
      const targetPath = l === 'vi' ? `/vi${enPath}` : enPath;
      setLocale(l);
      navigate(targetPath, { replace: true });
    } else {
      setLocale(l);
      if (isAuthenticated()) updateUserLocale(l);
    }
  };

  return (
    <div class="flex items-center gap-2">
      <For each={locales()}>
        {(l) => (
          <button
            onClick={() => select(l)}
            class={`flex items-center gap-1 rounded-md px-2 py-1 text-xs font-semibold transition-colors ${
              locale() === l
                ? 'bg-accent-primary text-text-inverse'
                : 'text-text-secondary hover:bg-bg-hover'
            }`}
          >
            {getFlag(l)} {l.toUpperCase()}
          </button>
        )}
      </For>
    </div>
  );
}
